import { useMemo } from 'react'
import { GeckoMarkupLayer } from 'types'

/**
 * Hook to count the number of scans from markup layers
 *
 * Looks for "Scan Labels" markup layer in the "Report" path and counts
 * the number of label primitives, which represent individual scans
 *
 * @returns The number of scans found in markup layers
 */
export const useScanCount = (
  markupLayersByInspection: ReadonlyMap<string, readonly GeckoMarkupLayer[]>
): ReadonlyMap<string, number> => {
  return useMemo(() => {
    const scanCountByInspection = new Map<string, number>()

    for (const [inspectionId, markupLayers] of markupLayersByInspection) {
      const scanLabelsLayers = markupLayers.filter(
        (layer) =>
          layer.data?.name === 'Scan Labels' &&
          Array.isArray(layer.data?.path) &&
          layer.data?.path[0] === 'Report'
      )

      if (scanLabelsLayers.length === 0) {
        scanCountByInspection.set(inspectionId, 0)
        continue
      }

      const scanCount = scanLabelsLayers
        .map((scanLabelsLayer) => scanLabelsLayer.data?.labelPrimitives.length)
        .filter((labelLength) => !!labelLength)
        .reduce((prev, curr) => prev + curr, 0)

      scanCountByInspection.set(inspectionId, scanCount)
    }

    return scanCountByInspection
  }, [markupLayersByInspection])
}
